import React from 'react';
import { Card, CardHeader, CardBody, Text, Flex, Box, Link } from '@backstage/ui';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { appTranslationRef } from '../../../../core/i18n';
import {
  GITLAB_ANNOTATIONS,
  parseReleasesAnnotation,
  GitLabRelease,
} from './gitlab.types';
import { EmptyState } from '../../../../shared/components';
import { useSourceLocation } from '../../hooks';
import styles from './gitlab.module.css';

function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
}

const ReleaseItem = ({ release, projectUrl }: { release: GitLabRelease; projectUrl?: string }) => {
  const { t } = useTranslationRef(appTranslationRef);
  const releaseUrl = projectUrl ? `${projectUrl}/-/releases/${release.tagName}` : undefined;

  const relativeTime = React.useMemo(() => {
    try {
      const date = new Date(release.releasedAt);
      const now = new Date();
      const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return t('relativeTime.today');
      if (diffDays === 1) return t('relativeTime.yesterday');
      if (diffDays < 7)
        return t('relativeTime.daysAgo').replace('{{count}}', String(diffDays));
      if (diffDays < 30)
        return t('relativeTime.weeksAgo').replace('{{count}}', String(Math.floor(diffDays / 7)));
      if (diffDays < 365)
        return t('relativeTime.monthsAgo').replace('{{count}}', String(Math.floor(diffDays / 30)));
      return t('relativeTime.yearsAgo').replace('{{count}}', String(Math.floor(diffDays / 365)));
    } catch {
      return '';
    }
  }, [release.releasedAt, t]);

  return (
    <Box className={styles.releaseRow}>
      <Flex direction="column" gap="2">
        <Flex justify="between" align="center" className={styles.releaseRowInner} gap="2">
          <Flex gap="2" align="center">
            <Box className={styles.releaseTag}>
              {release.tagName}
            </Box>
            {releaseUrl ? (
              <Link href={releaseUrl} target="_blank" variant="body-medium" weight="bold">
                {release.name || release.tagName}
              </Link>
            ) : (
              <Text variant="body-medium" weight="bold">
                {release.name || release.tagName}
              </Text>
            )}
          </Flex>
          <Text variant="body-small" color="secondary" title={formatDate(release.releasedAt)}>
            {relativeTime}
          </Text>
        </Flex>
      </Flex>
    </Box>
  );
};

export const GitLabReleasesCard = () => {
  const { entity } = useEntity();
  const { t } = useTranslationRef(appTranslationRef);

  const { projectUrl } = useSourceLocation();

  const releasesAnnotation =
    entity.metadata.annotations?.[GITLAB_ANNOTATIONS.RELEASES];
  const releases = parseReleasesAnnotation(releasesAnnotation);
  const releasesUrl = projectUrl ? `${projectUrl}/-/releases` : undefined;

  return (
    <Card>
      <CardHeader>
        <Flex justify="between" align="center">
          <Text variant="title-small" weight="bold">
            {t('gitlab.releases.title')}
          </Text>
          {releases && releases.length > 0 && (() => {
            const label = `${releases.length} ${releases.length === 1 ? t('gitlab.releases.release') : t('gitlab.releases.releases')}`;
            return releasesUrl
              ? <Link href={releasesUrl} target="_blank" variant="body-small" color="secondary">{label}</Link>
              : <Text variant="body-small" color="secondary">{label}</Text>;
          })()}
        </Flex>
      </CardHeader>
      <CardBody>
        {!releases || releases.length === 0 ? (
          <EmptyState
            title={t('gitlab.releases.emptyState.title')}
            description={t('gitlab.releases.emptyState.description')}
          />
        ) : (
          <Flex direction="column" gap="2">
            {releases.map((release, index) => (
              <ReleaseItem key={release.tagName || index} release={release} projectUrl={projectUrl} />
            ))}
          </Flex>
        )}
      </CardBody>
    </Card>
  );
};
